import { ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from './Button';

/**
 * ErrorState — variante de EmptyState con tono `error` (DESIGN-SYSTEM §3.22).
 *
 * Diferencias:
 *  - Icono / ilustracion en `text-error` por defecto si no se provee uno.
 *  - Acepta un `error?: Error` para mostrar `error.message`, pero ocultando el
 *    stack al usuario final (DESIGN-SYSTEM §3.22). El mensaje se renderiza
 *    como `description` si no se pasa una explicita.
 *  - CTA "Reintentar" opcional via `onRetry`.
 *  - ARIA: `role="alert"` para que SR anuncie inmediatamente.
 */
export interface ErrorStateProps {
  title?: string;
  description?: string;
  /** Si presente, su `message` se usa como description (cuando no hay una explicita). */
  error?: Error;
  /** Ilustracion personalizada. Si no, se usa `AlertOctagon` en `text-error`. */
  illustration?: ReactNode;
  /** Si presente, renderiza CTA "Reintentar" (label override via `retryLabel`). */
  onRetry?: () => void;
  retryLabel?: string;
  /** Aria-label explicito; default = title. */
  'aria-label'?: string;
  className?: string;
}

export function ErrorState({
  title = 'Algo salio mal',
  description,
  error,
  illustration,
  onRetry,
  retryLabel = 'Reintentar',
  className,
  'aria-label': ariaLabel,
}: ErrorStateProps) {
  // Resolvemos la descripcion: explicita > error.message > nada.
  // Nota DS §3.22: nunca mostrar stack trace al usuario final.
  const resolvedDescription = description ?? error?.message;

  const resolvedIllustration = illustration ?? (
    <AlertOctagon className="h-16 w-16" aria-hidden="true" />
  );

  return (
    <section
      role="alert"
      aria-live="assertive"
      aria-label={ariaLabel ?? title}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'py-12 px-6 gap-4',
        className,
      )}
    >
      <div className="text-error" aria-hidden="true">
        {resolvedIllustration}
      </div>
      <div className="max-w-md space-y-2">
        <h2 className="font-display text-h3 text-primary-900">{title}</h2>
        {resolvedDescription && (
          <p className="text-body text-primary-900/70">{resolvedDescription}</p>
        )}
      </div>
      {onRetry && (
        <div className="mt-2">
          <Button variant="primary" size="md" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
    </section>
  );
}
